import {
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlinedIcon,
  Edit as EditIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'src/components/menu';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { useGetAssemblyState, useGetEnvironment } from 'src/hooks';
import { AssemblyState, IStatus } from 'src/interfaces';
import { getIsDefaultAssembly, lang, useUserRole } from 'src/utils';
import { assemblyEditFromRowEvent } from 'src/utils/google-tag-manager';

import { AssemblyMenuProps } from './AssemblyMenu.interface';

export const AssemblyMenu = (props: AssemblyMenuProps) => {
  const navigate = useNavigate();
  const { isViewOnly, isOwnerOperator } = useUserRole();

  const { data: environment } = useGetEnvironment();

  const onGetLinkClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(
        `${environment?.urls.mtUI}/cc-ui/#${
          isViewOnly || isOwnerOperator
            ? PATHS.ASSEMBLY_DETAILS(props.row.id)
            : PATHS.ASSEMBLY_EDIT(props.row.id)
        }`,
      );

      Snackbar.success(`Link to ${props.row.name} ${lang.successfullyCopied}`);
    } catch (error) {
      Snackbar.error(`${lang.failedToCopyAssembly}`);
    }
    props.onClose();
  };
  const onEditClick = async () => {
    navigate({
      pathname: PATHS.ASSEMBLY_EDIT(props.row.id),
    });
    props.onClose();
  };

  const onDeleteClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    props.onDeleteClick();
    props.onClose();
  };

  const onDuplicateClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    props.onDuplicateClick();
    props.onClose();
  };

  const onConvertToDraftClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    props.onConvertToDraftClick();
    props.onClose();
  };

  const isDefaultAssembly = getIsDefaultAssembly(props.row);

  const {
    data: [assemblyState],
  } = useGetAssemblyState(isDefaultAssembly === null ? [props.row.id] : []);

  const items = [
    ...(props.row.status === IStatus.READY &&
    (isDefaultAssembly !== null
      ? !isDefaultAssembly
      : Boolean(assemblyState?.state === AssemblyState.UNUSED)) &&
    (props.row.childrenAssemblies?.length || 0) === 0 &&
    !props.hasStageAssembly &&
    !isOwnerOperator
      ? [
          {
            label: lang.convertToDraft,
            onClick: onConvertToDraftClick,
            leftIcon: <EditIcon />,
            disabled: isViewOnly,
          },
        ]
      : []),
    ...(props.row.status !== IStatus.READY &&
    !props.hasStageAssembly &&
    !isOwnerOperator
      ? [
          {
            label: lang.edit,
            onClick: () => {
              assemblyEditFromRowEvent(props.row.id);
              onEditClick();
            },
            leftIcon: <EditIcon />,
            disabled: isViewOnly,
          },
        ]
      : []),
    ...(!isOwnerOperator
      ? [
          {
            label: lang.duplicate,
            onClick: onDuplicateClick,
            leftIcon: <ContentCopyIcon />,
            disabled: isViewOnly,
          },
        ]
      : []),
    {
      label: lang.getLink,
      onClick: onGetLinkClick,
      leftIcon: <LinkIcon />,
    },

    ...(props.row.status !== IStatus.READY &&
    !props.hasStageAssembly &&
    !props.hasSavedAssembly &&
    !isOwnerOperator
      ? [
          {
            label: lang.deleteText,
            onClick: onDeleteClick,
            leftIcon: <DeleteOutlinedIcon />,
            disabled: isViewOnly,
          },
        ]
      : []),
  ];

  return (
    <>
      <Menu anchorEl={props.anchorEl} items={items} onClose={props.onClose} />
    </>
  );
};
